// src/components/health/timeline/HealthTimeline.tsx
// Main Timeline Component for Health Events

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, isToday, isYesterday, isSameDay } from 'date-fns';
import { 
  Calendar as CalendarIcon,
  ChevronDown,
  ChevronUp,
  Filter,
} from 'lucide-react';
import { TimelineEvent } from './TimelineEvent';
import { TimelineFilters } from './TimelineFilters';
import { TimelineLegend } from './TimelineLegend';
import type { 
  TimelineEvent as TimelineEventType, 
  TimelineDay,
  TimelineFilters as TimelineFiltersType,
  TimelineViewOptions,
} from '@/features/health/types/healthTypes/reportTypes';

// ============================================================================
// Types
// ============================================================================

interface HealthTimelineProps {
  events: TimelineEventType[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onEventClick?: (event: TimelineEventType) => void;
}

// ============================================================================
// Component
// ============================================================================

export const HealthTimeline: React.FC<HealthTimelineProps> = ({
  events = [],
  isLoading = false,
  onLoadMore,
  hasMore = false,
  onEventClick,
}) => {
  // Ensure events is always an array
  const safeEvents = Array.isArray(events) ? events : [];
  const [filters, setFilters] = useState<TimelineFiltersType>({
    types: [],
    categories: [],
  });
  const [viewOptions, setViewOptions] = useState<TimelineViewOptions>({
    groupBy: 'day',
    showMedications: true,
    showAppointments: true,
    expandAll: false,
    highlightSevere: true,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // Filter and group events
  const groupedEvents = useMemo(() => {
    let filtered = [...safeEvents];

    // Apply type filter
    if (filters.types.length > 0) {
      filtered = filtered.filter(e => filters.types.includes(e.type));
    }

    // Apply category filter
    if (filters.categories.length > 0) {
      filtered = filtered.filter(e => filters.categories.includes(e.category));
    }

    // Apply severity filter
    if (filters.severity) {
      filtered = filtered.filter(e => 
        e.severity !== undefined && 
        e.severity >= filters.severity!.min && 
        e.severity <= filters.severity!.max
      );
    }

    // Apply search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.title.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
      );
    }

    // Group by day
    const days: TimelineDay[] = [];
    let currentDate: string | null = null;
    let currentEvents: TimelineEventType[] = [];

    filtered.forEach(event => {
      const eventDate = format(event.timestamp, 'yyyy-MM-dd');
      
      if (currentDate !== eventDate) {
        if (currentDate !== null) {
          days.push({
            date: parseISO(currentDate),
            events: currentEvents,
            summary: {
              totalEvents: currentEvents.length,
              byType: countByType(currentEvents),
            },
          });
        }
        currentDate = eventDate;
        currentEvents = [event];
      } else {
        currentEvents.push(event);
      }
    });

    // Add last group
    if (currentDate !== null) {
      days.push({
        date: parseISO(currentDate),
        events: currentEvents,
        summary: {
          totalEvents: currentEvents.length,
          byType: countByType(currentEvents),
        },
      });
    }

    return days;
  }, [safeEvents, filters]);

  // Toggle day expansion
  const toggleDay = (dateStr: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) {
        next.delete(dateStr);
      } else {
        next.add(dateStr);
      }
      return next;
    });
  };

  // Check if day is expanded
  const isDayExpanded = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return viewOptions.expandAll || expandedDays.has(dateStr);
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center">
            <CalendarIcon className="w-5 h-5 text-sage-dark" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-navy">Health Timeline</h2>
            <p className="text-sm text-warm-gray">
              {safeEvents.length} events
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border
              ${showFilters 
                ? 'bg-sage text-white border-sage' 
                : 'bg-white text-navy border-cream-dark hover:border-sage-light'
              }
              transition-colors duration-200
            `}
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">Filters</span>
            {(filters.types.length > 0 || filters.categories.length > 0) && (
              <span className="
                w-5 h-5 rounded-full text-xs font-semibold
                flex items-center justify-center
                bg-terracotta text-white
              ">
                {filters.types.length + filters.categories.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setViewOptions(prev => ({ ...prev, expandAll: !prev.expandAll }))}
            className="
              flex items-center gap-2 px-4 py-2 rounded-lg border
              bg-white text-navy border-cream-dark hover:border-sage-light
              transition-colors duration-200
            "
          >
            {viewOptions.expandAll ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">
              {viewOptions.expandAll ? 'Collapse' : 'Expand'} All
            </span>
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <TimelineFilters
              filters={filters}
              onChange={setFilters}
              events={safeEvents}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <TimelineLegend events={safeEvents} />

      {/* Timeline Content */}
      {isLoading && safeEvents.length === 0 ? (
        <TimelineSkeleton />
      ) : groupedEvents.length === 0 ? (
        <EmptyTimeline />
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-cream-dark" />

          {/* Day Groups */}
          <div className="space-y-6">
            {groupedEvents.map((day, dayIndex) => (
              <DayGroup
                key={format(day.date, 'yyyy-MM-dd')}
                day={day}
                isExpanded={isDayExpanded(day.date)}
                onToggle={() => toggleDay(format(day.date, 'yyyy-MM-dd'))}
                onEventClick={onEventClick}
                highlightSevere={viewOptions.highlightSevere}
                index={dayIndex}
              />
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                onClick={onLoadMore}
                disabled={isLoading}
                className="
                  px-6 py-2.5 rounded-lg border border-cream-dark
                  bg-white hover:bg-cream text-navy font-medium text-sm
                  transition-colors duration-200 disabled:opacity-50
                "
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Day Group Component
// ============================================================================

interface DayGroupProps {
  day: TimelineDay;
  isExpanded: boolean;
  onToggle: () => void;
  onEventClick?: (event: TimelineEventType) => void;
  highlightSevere: boolean;
  index: number;
}

const DayGroup: React.FC<DayGroupProps> = ({
  day,
  isExpanded,
  onToggle,
  onEventClick,
  highlightSevere,
  index,
}) => {
  const dateLabel = formatDateLabel(day.date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      {/* Day Header */}
      <button
        onClick={onToggle}
        className="
          relative flex items-center gap-4 w-full
          group hover:opacity-80 transition-opacity
        "
      >
        {/* Date Circle */}
        <div className="
          relative z-10 w-12 h-12 rounded-full
          bg-white border-2 border-sage
          flex flex-col items-center justify-center
        ">
          <span className="text-lg font-display font-semibold text-sage-dark leading-none">
            {format(day.date, 'd')}
          </span>
          <span className="text-[10px] uppercase text-warm-gray leading-none">
            {format(day.date, 'MMM')}
          </span>
        </div>

        {/* Date Label & Summary */}
        <div className="flex-1 flex items-center justify-between py-3">
          <div>
            <p className="font-medium text-navy">{dateLabel}</p>
            <p className="text-sm text-warm-gray">
              {day.summary?.totalEvents || 0} events
            </p>
          </div>

          {/* Expand/Collapse Icon */}
          <div className="text-warm-gray">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </div>
        </div>
      </button>

      {/* Events */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="ml-14 space-y-2 pt-2"
          >
            {day.events.map((event, eventIndex) => (
              <TimelineEvent
                key={event.id}
                event={event}
                onClick={() => onEventClick?.(event)}
                highlightSevere={highlightSevere}
                index={eventIndex}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================================================
// Empty State
// ============================================================================

const EmptyTimeline: React.FC = () => (
  <div className="py-12 text-center">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cream flex items-center justify-center">
      <CalendarIcon className="w-8 h-8 text-warm-gray" />
    </div>
    <p className="font-medium text-navy">No events found</p>
    <p className="text-sm text-warm-gray mt-1">
      Try adjusting your filters or date range.
    </p>
  </div>
);

// ============================================================================
// Loading Skeleton
// ============================================================================

const TimelineSkeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex gap-4">
        <div className="w-12 h-12 rounded-full bg-cream-dark" />
        <div className="flex-1 space-y-2">
          <div className="h-5 w-32 bg-cream-dark rounded" />
          <div className="h-4 w-24 bg-cream-dark rounded" />
          <div className="space-y-2 pt-2">
            {[...Array(2)].map((_, j) => (
              <div key={j} className="h-16 bg-cream-dark rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// Helper Functions
// ============================================================================

function formatDateLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEEE, MMMM d, yyyy');
}

function countByType(events: TimelineEventType[]): Record<string, number> {
  return events.reduce((acc, event) => {
    acc[event.type] = (acc[event.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

export default HealthTimeline;

